import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import {
  periodDescription,
  periodInput,
  periodReady,
  ReportPeriodPicker,
  thisMonthPeriod,
  type ReportPeriod,
} from "@/components/app/documents/report-period";
import { ReportExport } from "@/components/app/documents/report-export";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { YearOverviewGrid } from "@/components/app/lists/year-overview-grid";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { cn } from "@/lib/utils";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import {
  periodFromSearch,
  periodSearch,
  periodToSearch,
  type PeriodSearch,
} from "@/lib/facility/report-period-search";
import { optionalString, unlessAll, KEEP_PLACE, optionalSearch } from "@/lib/search";
import {
  DEFAULT_ROLL_FILTERS,
  rollFilterInput,
  rollIsFiltered,
  type RollFilterState,
} from "./-components/roll-filters";
import { RollTable } from "./-components/roll-table";
import { RollToolbar } from "./-components/roll-toolbar";
import { YearPaymentsGrid } from "./-components/year-payments-grid";
import type { RollRow } from "./-components/roll-types";

type RollSearch = PeriodSearch & {
  q?: string;
  resident?: string;
  payment?: RollFilterState["paymentStatus"];
  aging?: string;
};

export const Route = createFileRoute("/_authenticated/_financials/rent-roll/")({
  validateSearch: (search: Record<string, unknown>): RollSearch => ({
    ...periodSearch(search),
    q: optionalSearch(search.q),
    resident: optionalString(search.resident)?.slice(0, 40) || undefined,
    payment: (optionalString(search.payment)?.slice(0, 40) || undefined) as
      | RollFilterState["paymentStatus"]
      | undefined,
    aging: optionalString(search.aging)?.slice(0, 40) || undefined,
  }),
  component: RentRoll,
});

const withoutSearch = (key: readonly unknown[]) =>
  JSON.stringify(key, (name, value: unknown) => (name === "search" ? undefined : value));

/** Who is in the building, what they were charged, and what they owe. */
function RentRoll() {
  const { facilityId } = useFacility();
  // Open on this month and wait for Run, so a large building is not read in
  // full just by visiting. Changing the period asks
  // again; the refine filters below re-filter what is loaded.
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<RollSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const fallback = thisMonthPeriod();
  const period = periodFromSearch(params, fallback);
  const [ran, setRan] = useState(false);
  const setPeriod = (next: ReportPeriod) => {
    patch(periodToSearch(next, fallback));
    setRan(false);
  };
  const {
    text: searchText,
    change: setSearchText,
    settling,
  } = useSearchParamText(params.q, (q) => patch({ q }));
  const filterState: RollFilterState = {
    residentStatus: params.resident ?? DEFAULT_ROLL_FILTERS.residentStatus,
    paymentStatus: params.payment ?? DEFAULT_ROLL_FILTERS.paymentStatus,
    agingBucket: params.aging ?? DEFAULT_ROLL_FILTERS.agingBucket,
    search: searchText,
  };
  const setFilter = (change: Partial<RollFilterState>) => {
    if (change.search !== undefined) setSearchText(change.search);
    patch({
      ...(change.residentStatus !== undefined
        ? { resident: unlessAll(change.residentStatus) }
        : {}),
      ...(change.paymentStatus !== undefined
        ? { payment: unlessAll(change.paymentStatus) as RollSearch["payment"] }
        : {}),
      ...(change.agingBucket !== undefined ? { aging: unlessAll(change.agingBucket) } : {}),
    });
  };

  const yearView = period.view === "yearOverview";
  const filters = rollFilterInput(period.view, filterState);
  const input = { facilityId, ...periodInput(period), ...filters };
  const term = params.q ?? "";
  const rollOptions = orpc.financials.reports.rentRoll.queryOptions({
    input: { ...input, ...(period.view === "yearOverview" ? {} : { search: term || undefined }) },
  });
  /* Only a change of search keeps the old rows; a new period or filter must not show another
   * report's rows as this one's. */
  const roll = useQuery({
    ...rollOptions,
    enabled: ran && periodReady(period),
    placeholderData: (previous, previousQuery) =>
      previousQuery && withoutSearch(previousQuery.queryKey) === withoutSearch(rollOptions.queryKey)
        ? previous
        : undefined,
  });
  const searching = settling || roll.isPlaceholderData;

  // The screen and the file are one report: the export takes the same filters,
  // so a spreadsheet cannot disagree with the page it came from.
  const download = useMutation(
    orpc.financials.reports.rentRollExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const detailById = new Map(
    (roll.data?.residentDetails ?? []).map((detail) => [detail.residentId, detail]),
  );
  const rows: RollRow[] = (roll.data?.residents ?? []).map((resident) => ({
    resident,
    detail: detailById.get(resident.id),
  }));
  const totals = roll.data
    ? {
        residentCount: rows.length,
        breakdown: roll.data.revenueBreakdown,
        balances: roll.data.balanceTotals,
      }
    : null;
  const filtered = rollIsFiltered(filterState);

  return (
    <>
      <PageHeader
        title="Rent roll"
        meta={
          roll.data
            ? `${periodDescription(period)}, as at ${roll.data.asOf}`
            : "Who is in the building, and what they owe."
        }
        actions={
          <ReportExport
            labels={{ csv: "Full period CSV", pdf: "Print full period" }}
            pending={download.isPending}
            onExport={(format) => download.mutate({ ...input, format })}
          />
        }
      />

      <div className="space-y-1">
        <div className="flex flex-wrap items-end gap-3">
          <ReportPeriodPicker period={period} onChange={setPeriod} />
          <Button onClick={() => setRan(true)} disabled={!periodReady(period) || ran}>
            Run report
          </Button>
        </div>
        <p className="text-muted-foreground text-sm">{periodDescription(period)}</p>
      </div>

      {roll.data?.stats ? (
        <StatTiles>
          <StatTile label="Rent billed" value={roll.data.stats.totalMonthlyRent} />
          <StatTile label="Care billed" value={roll.data.stats.totalMonthlyCare} />
          <StatTile label="Outstanding" value={roll.data.stats.totalOutstandingBalance} />
          <StatTile
            label="Collected"
            value={`${roll.data.stats.collectionRate}%`}
            hint="Of what was billed"
          />
          <StatTile
            label="Overdue"
            value={roll.data.stats.overdueCount}
            hint="Residents behind on payment"
            tone={roll.data.stats.overdueCount > 0 ? "crit" : undefined}
          />
        </StatTiles>
      ) : null}

      {!ran ? (
        <EmptyState
          title="Nothing loaded yet"
          description="Pick a reporting period, then run the report."
        />
      ) : roll.isError ? (
        <ReadFailed
          message="The rent roll could not be loaded."
          error={roll.error}
          onRetry={() => void roll.refetch()}
          retrying={roll.isFetching}
        />
      ) : yearView ? (
        roll.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : roll.data?.yearOverview ? (
          <>
            <YearOverviewGrid
              year={roll.data.yearOverview.year}
              months={roll.data.yearOverview.monthlyData}
              totals={roll.data.yearOverview.yearTotals}
            />
            <YearPaymentsGrid months={roll.data.yearOverview.monthlyData} />
          </>
        ) : null
      ) : (
        <>
          <RollToolbar value={filterState} onChange={setFilter} />

          <div
            aria-busy={searching || undefined}
            className={cn("transition-opacity", searching && "opacity-60")}
          >
            <RollTable rows={rows} totals={totals} pending={roll.isPending} filtered={filtered} />
          </div>
        </>
      )}
    </>
  );
}
